import { Resource } from '../Resource';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { CHART_REQUEST_INVALIDATION_KEY } from './charts';
const irritensioPath = 'oad/irritensio';
const rootPath = `${irritensioPath}/thresholds`;
const defaultThresholdsPath = `${irritensioPath}/default_thresholds`;
const orgThresholdsProfilePath = `${irritensioPath}/custom_threshold`;
const thresholdPlotLinkPath = `${irritensioPath}/threshold_plot_link`;
const fromRaw = {
    getThresholds: (rawEditThresholds) => rawEditThresholds,
    getDefaultThresholds: (rawDefaultThresholds) => rawDefaultThresholds,
    getThresholdsProfile: (raw) => {
        return {
            id: raw.id,
            name: raw.name,
            thresholds: raw.thresholds,
            linkedPlotNames: raw.plots,
            metric: raw.metric
        };
    },
    getThresholdsProfilePlotLink: (raw) => {
        return {
            id: raw.id,
            plotId: raw.plot,
            createdAt: raw.custom_threshold_created_at,
            thresholds: raw.custom_threshold,
            thresholdsProfileId: raw.custom_threshold_id,
            thresholdsProfileName: raw.custom_threshold_name
        };
    }
};
const toRaw = {
    updateIrritensio: (editThresholds) => {
        return { ...editThresholds };
    },
    updateOrCreateThresholdsProfile: (thresholdsProfilePayload) => {
        return {
            name: thresholdsProfilePayload.name,
            thresholds: thresholdsProfilePayload.thresholds,
            metric: thresholdsProfilePayload.metric
        };
    },
    linkThresholdsProfileToPlot: (payload) => {
        return {
            plot: payload.plotId,
            threshold: payload.thresholdsId
        };
    }
};
const thresholds = Resource((requesterBuilder) => {
    const getIrritensio = (plotId, metric) => {
        return methods.get({
            rootPath,
            requesterBuilder,
            fromRaw: fromRaw.getThresholds,
            params: { query: { plot: plotId, metrics: metric } }
        })();
    };
    const getManyThresholdsProfiles = (query) => requesterBuilder({
        path: orgThresholdsProfilePath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw.getThresholdsProfile),
            params: {
                query: {
                    organization: query?.orgId,
                    metric: query?.metric,
                    limit: query?.limit,
                    offset: query?.offset
                }
            }
        }
    });
    const getAllThresholdsProfiles = methods.getAllPages({
        getMany: getManyThresholdsProfiles
    });
    const getManyThresholdsProfilePlotLinks = (query) => requesterBuilder({
        path: thresholdPlotLinkPath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw.getThresholdsProfilePlotLink),
            params: {
                query: {
                    organization: query?.orgId,
                    plot: query?.plotId
                }
            }
        }
    });
    const getThresholdsProfilePlotLinks = methods.getAllPages({
        getMany: getManyThresholdsProfilePlotLinks
    });
    const getDefaultThresholds = (query) => requesterBuilder({
        path: defaultThresholdsPath,
        requestCtx: {
            transform: fromRaw.getDefaultThresholds,
            params: {
                query: {
                    metric: query.metric
                }
            }
        },
        config: {
            // Result will probably never change so we use a long cache for it
            staleTime: 60 * 60 * 1000
        }
    });
    const linksInvalidationKeys = [
        getThresholdsProfilePlotLinks().invalidationKey,
        getManyThresholdsProfilePlotLinks().invalidationKey,
        CHART_REQUEST_INVALIDATION_KEY
    ];
    const buildKeysToInvalidateOnThresholdsProfileLinksEdition = () => linksInvalidationKeys;
    const buildKeysToInvalidateOnThresholdsProfileEdition = () => [
        getManyThresholdsProfiles().invalidationKey,
        getAllThresholdsProfiles().invalidationKey,
        ...linksInvalidationKeys
    ];
    return {
        updateIrritensio: (plotId, metric) => {
            return methods.update({
                rootPath,
                requesterBuilder,
                fromRaw: fromRaw.getThresholds,
                toRaw: toRaw.updateIrritensio,
                params: { query: { plot: plotId, metrics: metric } },
                buildKeysToInvalidate: () => [getIrritensio(plotId, metric).key]
            })();
        },
        getIrritensio,
        getDefaultThresholds,
        getManyThresholdsProfiles,
        getAllThresholdsProfiles,
        getThresholdsProfile: methods.get({
            rootPath: orgThresholdsProfilePath,
            fromRaw: fromRaw.getThresholdsProfile,
            requesterBuilder
        }),
        createThresholdsProfile: (orgId) => methods.create({
            rootPath: orgThresholdsProfilePath,
            fromRaw: fromRaw.getThresholdsProfile,
            requesterBuilder,
            toRaw: toRaw.updateOrCreateThresholdsProfile,
            params: { query: { organization: orgId } },
            buildKeysToInvalidate: buildKeysToInvalidateOnThresholdsProfileEdition
        })(),
        updateThresholdsProfile: methods.update({
            rootPath: orgThresholdsProfilePath,
            fromRaw: fromRaw.getThresholdsProfile,
            requesterBuilder,
            toRaw: toRaw.updateOrCreateThresholdsProfile
        }),
        deleteThresholdsProfile: methods.deletion({
            rootPath: orgThresholdsProfilePath,
            requesterBuilder,
            buildKeysToInvalidate: buildKeysToInvalidateOnThresholdsProfileEdition
        }),
        getThresholdsProfilePlotLinks,
        linkThresholdsProfileToPlot: (orgId) => methods.create({
            rootPath: thresholdPlotLinkPath,
            fromRaw: fromRaw.getThresholdsProfilePlotLink,
            requesterBuilder,
            toRaw: toRaw.linkThresholdsProfileToPlot,
            params: { query: { organization: orgId } },
            buildKeysToInvalidate: buildKeysToInvalidateOnThresholdsProfileLinksEdition
        })(),
        /** Needs the threshold and threshold plot link id */
        removeThresholdsProfileFromPlot: methods.deletion({
            rootPath: thresholdPlotLinkPath,
            requesterBuilder,
            buildKeysToInvalidate: buildKeysToInvalidateOnThresholdsProfileLinksEdition
        })
    };
});
export default thresholds;
